import { ArcLayer } from "@deck.gl/layers";
import { MapboxLayer } from "@deck.gl/mapbox";
import { getDatasourceById } from "@/utils";

const zonesConfig = {
  only: ["id", "geom", "center_geom", "no"]
};

const getArcsConfig = limit => ({
  order: [
    {
      value: "asc"
    }
  ],
  limit
});

function getRGBValues(str) {
  const values = str.substring(str.indexOf("(") + 1, str.length - 1).split(",");

  return values.map((v, i) =>
    i !== values.length - 1 ? Number(v) : Math.floor(255 * v)
  );
}

export class MatricesController {
  constructor(map) {
    this.map = map;
    this.mapgl = map.mapgl;
    this.$store = map.$store;
    this.styleConfig = map.styleConfig;

    // this.zoneClickHandler = this.zoneClickHandler.bind(this);
  }

  // get data helpers
  async getMatrices(config, modelId, id) {
    if (!config.matricesSourceId) {
      const model = await getDatasourceById(this.map, modelId, {
        chidlren: {}
      });
      const matricesSource = model.children.find(
        c => c.datatype === "matrices"
      );

      this.map.$set(config, "matricesSourceId", matricesSource.id);
    }

    let matrices;

    if (this.$store.state.map.matricesList[id]) {
      matrices = this.$store.state.map.matricesList[id];
    } else {
      const matricesUrl = `objectInfo/${config.matricesSourceId}`;
      const { data } = await this.$store.dispatch("REQUEST", {
        url: matricesUrl
      });

      matrices = Object.values(data);

      this.$store.commit("SET_MATRICES_LIST", {
        layerId: id,
        list: matrices
      });
    }

    return matrices;
  }

  async getZones(id) {
    let zones;

    if (this.$store.state.map.zonesList[id]) {
      zones = this.$store.state.map.zonesList[id];
    } else {
      const sourceId = this.$store.state.profiles.sourceIdById[id];
      const zonesUrl = `objectInfo/${sourceId}?config=${JSON.stringify(
        zonesConfig
      )}`;
      const { data } = await this.$store.dispatch("REQUEST", {
        url: zonesUrl
      });

      zones = Object.values(data);

      this.$store.commit("SET_ZONES_LIST", {
        layerId: id,
        list: zones
      });
    }

    return zones;
  }

  // get arcs data
  async getMatricesData(modelId, id, config) {
    try {
      // get matrices
      const matrices = await this.getMatrices(config, modelId, id);

      let activeMatrix;

      if (config.matrixId) {
        activeMatrix = config.matrixId;
      } else {
        activeMatrix = matrices[0].id;
        this.map.$set(config, "matrixId", activeMatrix);
      }

      // get zones
      await this.getZones(id);

      // get arcs data
      let arcsUrl = `network_matrix/${modelId}?matrix_id=${activeMatrix}&config=${JSON.stringify(
        getArcsConfig(config.count)
      )}`;
      if (config.zoneId) arcsUrl += `&fromzone_id=${config.zoneId}`;

      const { data } = await this.$store.dispatch("REQUEST", {
        url: arcsUrl
      });
      const arcs = data;

      if (!arcs) return null;

      // get min and max
      let min = arcs[0].value;
      let max = arcs[0].value;

      arcs.forEach(({ value }) => {
        if (value < min) min = value;
        if (value > max) max = value;
      });

      this.map.$set(config, "min", min);
      this.map.$set(config, "max", max);

      return arcs;
    } catch (error) {
      console.log(error);

      return null;
    }
  }

  getLabelsData(data) {
    if (!data) return null;

    return {
      type: "FeatureCollection",
      features: data.map(f => ({
        type: "Feature",
        geometry: f.from_geom,
        properties: {
          value: Number(f.value).toFixed(2)
        }
      }))
    };
  }

  getLayerOptions(id) {
    const config = this.styleConfig[id];
    const { matrices } = config;
    const { strokeWidth, sourceColor, targetColor, min, max } = matrices;

    return {
      getSourcePosition: d => d.from_geom.coordinates,
      getTargetPosition: d => d.to_geom.coordinates,
      getSourceColor: getRGBValues(sourceColor),
      getTargetColor: getRGBValues(targetColor),
      widthUnits: "pixels",
      widthScale: 1,
      getWidth: d => {
        const { value } = d;
        const part = (value - min) / (max - min);

        return part * strokeWidth;
      }
    };
  }

  async toggleMatrices(id) {
    const config = this.styleConfig[id];
    const { matrices } = config;
    const { enabled, modelId } = matrices;
    const mapLayerId = `${id}_matrices`;
    const labelsLayerId = `${id}_matrices_labels`;
    const circlesLayerId = `${id}_matrices_circles`;
    const mapLayer = this.mapgl.getLayer(mapLayerId);

    if (enabled && !mapLayer) {
      const data = await this.getMatricesData(modelId, id, matrices);
      const labelsData = this.getLabelsData(data);
      const options = this.getLayerOptions(id);
      const layer = new MapboxLayer({
        id: mapLayerId,
        type: ArcLayer,
        data,
        ...options
      });

      if (labelsData) {
        this.mapgl.addSource(labelsLayerId, {
          type: "geojson",
          data: labelsData
        });
        this.mapgl.addLayer({
          id: labelsLayerId,
          source: labelsLayerId,
          type: "symbol",
          paint: {
            "text-color": "#eee",
            "text-halo-color": "#202",
            "text-halo-width": 1
          },
          layout: {
            "text-field": ["get", "value"],
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            "text-size": 7,
            "text-letter-spacing": 0.05,
            "text-pitch-alignment": "map",
            "text-rotation-alignment": "map"
          }
        });
      }

      this.mapgl.addLayer(layer);
    } else {
      if (this.mapgl.getLayer(mapLayerId)) this.mapgl.removeLayer(mapLayerId);
      if (this.mapgl.getLayer(labelsLayerId))
        this.mapgl.removeLayer(labelsLayerId);
      if (this.mapgl.getLayer(circlesLayerId))
        this.mapgl.removeLayer(circlesLayerId);
      if (this.mapgl.getSource(labelsLayerId))
        this.mapgl.removeSource(labelsLayerId);
      this.map.$set(matrices, "enabled", false);
    }
  }

  async changeData(id) {
    const config = this.styleConfig[id];
    const { matrices } = config;
    const { modelId } = matrices;
    const mapLayerId = `${id}_matrices`;
    const labelsLayerId = `${id}_matrices_labels`;
    const mapLayer = this.mapgl.getLayer(mapLayerId);

    if (!mapLayer) return;

    const data = await this.getMatricesData(modelId, id, matrices);
    const labelsData = this.getLabelsData(data);
    const options = this.getLayerOptions(id);

    mapLayer.implementation.setProps({
      data,
      ...options
    });

    const labelsSource = this.mapgl.getSource(labelsLayerId);

    if (!labelsSource) return;

    labelsSource.setData(labelsData);
  }

  changeProperty(id) {
    const mapLayerId = `${id}_matrices`;
    const mapLayer = this.mapgl.getLayer(mapLayerId);

    if (!mapLayer) return;

    const options = this.getLayerOptions(id);
    const { implementation } = mapLayer;
    const { data } = implementation.props;

    this.mapgl.removeLayer(mapLayerId);

    const layer = new MapboxLayer({
      id: mapLayerId,
      type: ArcLayer,
      data,
      ...options
    });

    this.mapgl.addLayer(layer);
  }
}
